import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";

export type ManagementCenterView = "single" | "multi";

const VIEW_SEARCH_PARAM = "view";
const MC_VIEW_STORAGE_KEY = "mcView";

/**
 * Single/Multi Management Center view, persisted in the `?view=` URL param (same pattern as
 * `pickupsGroupBy` in `useManagementCenterFilters`). "single" is the default and is kept out of
 * the URL; only the "multi" override is pinned, written with `{ replace: true }`.
 */
export const useManagementCenterView = ({ trackPrefix }: { trackPrefix: string }) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { track } = useAnalytics();
	const [mcView, setMcViewRaw] = useState<ManagementCenterView>(() => {
		const urlView = searchParams.get(VIEW_SEARCH_PARAM);
		if (urlView === "multi" || urlView === "single") {
			localStorage.setItem(MC_VIEW_STORAGE_KEY, urlView);
			return urlView;
		}
		const storedView = localStorage.getItem(MC_VIEW_STORAGE_KEY);
		return storedView === "multi" ? "multi" : "single";
	});
	const onMcViewChange = useCallback(
		(view: ManagementCenterView) => {
			setMcViewRaw(view);
			localStorage.setItem(MC_VIEW_STORAGE_KEY, view);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (view === "multi") next.set(VIEW_SEARCH_PARAM, "multi");
					else next.delete(VIEW_SEARCH_PARAM);
					return next;
				},
				{ replace: true },
			);
			track(`${trackPrefix}_view_toggled`, { view });
		},
		[setSearchParams, track, trackPrefix],
	);
	return { mcView, onMcViewChange };
};
